import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { useBlockUser, useSendFriendRequest, useUserSearch } from '../hooks/useFriends'
import { UserSearchResultRow } from './UserSearchResultRow'

/**
 * Recherche par pseudo exact. La requête part à la validation, et non à chaque
 * frappe : l'API n'accepte pas la recherche partielle, un appel par caractère
 * serait du bruit.
 */
export function UserSearch() {
  const { t } = useTranslation()
  const [draft, setDraft] = useState('')
  const [submitted, setSubmitted] = useState('')

  const { data: results, isFetching } = useUserSearch(submitted)
  const invite = useSendFriendRequest()
  const block = useBlockUser()

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSubmitted(draft.trim())
  }

  const hasSearched = submitted.length > 0 && !isFetching

  return (
    <section aria-labelledby="social-search-title" className="flex flex-col gap-4">
      <h2 id="social-search-title" className="font-display text-lg font-semibold text-on-surface">
        {t('social.search.title')}
      </h2>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3 md:flex-row md:items-end">
        <div className="flex-1">
          <Input
            id="social-search-username"
            label={t('social.search.label')}
            placeholder={t('social.search.placeholder')}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            helperText={t('social.search.helper')}
          />
        </div>
        <Button type="submit" disabled={draft.trim() === ''} isLoading={isFetching}>
          {t('social.search.submit')}
        </Button>
      </form>

      {invite.isError && <Alert variant="error">{t('social.search.invite_error')}</Alert>}
      {invite.isSuccess && <Alert variant="success">{t('social.search.invite_sent')}</Alert>}

      {hasSearched && results?.length === 0 && (
        <p className="rounded-2xl bg-surface-low px-4 py-6 text-center text-sm text-on-surface-variant">
          {t('social.search.no_result')}
        </p>
      )}

      {results && results.length > 0 && (
        <ul className="flex flex-col gap-2">
          {results.map((result) => (
            <UserSearchResultRow
              key={result.id}
              result={result}
              onInvite={(id) => invite.mutate(id)}
              onBlock={(id) => block.mutate(id)}
              isInviting={invite.isPending}
            />
          ))}
        </ul>
      )}
    </section>
  )
}
